import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Save,
  Upload,
  Image as ImageIcon,
  X,
  Plus,
  Trash2,
  Loader2,
  FileText,
  CreditCard,
  Settings,
  Phone,
  Mail,
  MapPin,
  Globe,
  Calendar,
  Users,
  GraduationCap
} from 'lucide-react';
import { admissionsService } from '../../firebase/admissions';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';
import R2Image from '../../components/R2Image';

const InstitutionCustomization = () => {
  const { currentUser, userProfile } = useRealtime();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customization, setCustomization] = useState({
    name: '',
    tagline: '',
    logo: '',
    bannerImage: '',
    about: '',
    features: [],
    programs: [],
    customSections: []
  });

  const [institutionData, setInstitutionData] = useState({
    name: '',
    type: 'coaching',
    description: '',
    applicationFee: 500,
    address: {
      street: '',
      city: '',
      state: '',
      pincode: ''
    },
    contact: {
      phone: '',
      email: '',
      website: ''
    },
    established: '',
    students: '',
    isActive: true
  });

  const [applicationSettings, setApplicationSettings] = useState({
    requirePayment: true,
    customFields: [],
    requirements: [],
    instructions: ''
  });

  const [newFeature, setNewFeature] = useState('');
  const [newProgram, setNewProgram] = useState('');
  const [newSection, setNewSection] = useState({ title: '', content: '' });
  const [newCustomField, setNewCustomField] = useState({ label: '', type: 'text', required: false });
  const [newRequirement, setNewRequirement] = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    loadCustomization();
  }, []);

  const loadCustomization = async () => {
    try {
      setLoading(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      
      if (!institutionId) {
        // Check if we need to create institution profile
        const needsSetup = !userProfile?.coachingId && !userProfile?.institutionId;
        if (needsSetup) {
          toast('Please set up your institution profile first', { icon: 'ℹ️' });
          setActiveTab('setup');
        }
        return;
      }

      // Load institution data
      const instResult = await admissionsService.getInstitutionById(institutionId);
      if (instResult.success) {
        setInstitutionData({
          name: instResult.institution.name || userProfile?.coachingName || '',
          type: instResult.institution.type || 'coaching',
          description: instResult.institution.description || '',
          applicationFee: instResult.institution.applicationFee || 500,
          address: instResult.institution.address || { street: '', city: '', state: '', pincode: '' },
          contact: instResult.institution.contact || { phone: '', email: '', website: '' },
          established: instResult.institution.established || '',
          students: instResult.institution.students || '',
          isActive: instResult.institution.isActive !== undefined ? instResult.institution.isActive : true
        });
      } else {
        // Institution doesn't exist, create from user profile
        setInstitutionData({
          name: userProfile?.coachingName || '',
          type: 'coaching',
          description: '',
          applicationFee: 500,
          address: userProfile?.address || { street: '', city: '', state: '', pincode: '' },
          contact: {
            phone: userProfile?.phone || '',
            email: userProfile?.email || currentUser?.email || '',
            website: ''
          },
          established: '',
          students: '',
          isActive: true
        });
      }

      // Load customization
      const result = await admissionsService.getInstitutionCustomization(institutionId);
      if (result.success && result.customization) {
        setCustomization(result.customization);
        if (result.customization.applicationSettings) {
          setApplicationSettings(result.customization.applicationSettings);
        }
      } else {
        setCustomization({
          name: institutionData.name || '',
          tagline: '',
          logo: '',
          bannerImage: '',
          about: institutionData.description || '',
          features: [],
          programs: [],
          customSections: []
        });
      }
    } catch (error) {
      toast.error('Error loading customization');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCustomization(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (file, imageType) => {
    try {
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      if (!institutionId) {
        toast.error('No institution ID found');
        return;
      }

      setSaving(true);
      const result = await admissionsService.uploadCustomizationImage(file, institutionId, imageType);
      
      if (result.success) {
        setCustomization(prev => ({
          ...prev,
          [imageType]: result.url
        }));
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      toast.error('Error uploading image');
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setCustomization(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setCustomization(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addProgram = () => {
    if (newProgram.trim()) {
      setCustomization(prev => ({
        ...prev,
        programs: [...prev.programs, newProgram.trim()]
      }));
      setNewProgram('');
    }
  };

  const removeProgram = (index) => {
    setCustomization(prev => ({
      ...prev,
      programs: prev.programs.filter((_, i) => i !== index)
    }));
  };

  const addCustomSection = () => {
    if (newSection.title.trim() && newSection.content.trim()) {
      setCustomization(prev => ({
        ...prev,
        customSections: [...prev.customSections, { ...newSection }]
      }));
      setNewSection({ title: '', content: '' });
    }
  };

  const removeCustomSection = (index) => {
    setCustomization(prev => ({
      ...prev,
      customSections: prev.customSections.filter((_, i) => i !== index)
    }));
  };

  const handleSaveInstitution = async () => {
    try {
      setSaving(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      
      if (!institutionId) {
        // Create new institution
        const result = await admissionsService.saveInstitution(institutionData);
        if (result.success) {
          toast.success('Institution profile created successfully');
          // Update user profile with institutionId
          // This would typically be done through a user update service
          window.location.reload(); // Reload to get new institution ID
        } else {
          toast.error('Failed to create institution');
        }
      } else {
        // Update existing institution
        const result = await admissionsService.saveInstitution(institutionData, institutionId);
        if (result.success) {
          toast.success('Institution profile updated successfully');
        } else {
          toast.error('Failed to update institution');
        }
      }
    } catch (error) {
      toast.error('Error saving institution');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCustomization = async () => {
    try {
      setSaving(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      
      if (!institutionId) {
        toast.error('Please set up your institution profile first');
        setActiveTab('setup');
        return;
      }

      const customizationData = {
        ...customization,
        applicationSettings
      };

      const result = await admissionsService.saveInstitutionCustomization(institutionId, customizationData);
      
      if (result.success) {
        toast.success('Customization saved successfully');
      } else {
        toast.error('Failed to save customization');
      }
    } catch (error) {
      toast.error('Error saving customization');
    } finally {
      setSaving(false);
    }
  };

  const addCustomField = () => {
    if (newCustomField.label.trim()) {
      setApplicationSettings(prev => ({
        ...prev,
        customFields: [...prev.customFields, { ...newCustomField }]
      }));
      setNewCustomField({ label: '', type: 'text', required: false });
    }
  };

  const removeCustomField = (index) => {
    setApplicationSettings(prev => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index)
    }));
  };

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setApplicationSettings(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (index) => {
    setApplicationSettings(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admissions Setup & Customization</h1>
            <p className="text-gray-600">
              Set up your institution profile and customize your admissions page
            </p>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-md p-2 mb-6 flex gap-2">
            <button
              onClick={() => setActiveTab('setup')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'setup'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Institution Setup
            </button>
            <button
              onClick={() => setActiveTab('display')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'display'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ImageIcon className="w-4 h-4 inline mr-2" />
              Display Customization
            </button>
            <button
              onClick={() => setActiveTab('application')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'application'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Application Process
            </button>
          </div>

          {/* Setup Tab */}
          {activeTab === 'setup' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Basic Info */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Institution Name *</label>
                    <input
                      type="text"
                      value={institutionData.name}
                      onChange={(e) => setInstitutionData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                    <select
                      value={institutionData.type}
                      onChange={(e) => setInstitutionData(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="school">School</option>
                      <option value="college">College</option>
                      <option value="coaching">Coaching</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={institutionData.description}
                      onChange={(e) => setInstitutionData(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Established Year</label>
                      <input
                        type="text"
                        value={institutionData.established}
                        onChange={(e) => setInstitutionData(prev => ({ ...prev, established: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Number of Students</label>
                      <input
                        type="text"
                        value={institutionData.students}
                        onChange={(e) => setInstitutionData(prev => ({ ...prev, students: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={institutionData.contact.phone}
                        onChange={(e) => setInstitutionData(prev => ({
                          ...prev,
                          contact: { ...prev.contact, phone: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={institutionData.contact.email}
                        onChange={(e) => setInstitutionData(prev => ({
                          ...prev,
                          contact: { ...prev.contact, email: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                    <input
                      type="url"
                      value={institutionData.contact.website}
                      onChange={(e) => setInstitutionData(prev => ({
                        ...prev,
                        contact: { ...prev.contact, website: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Address</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                    <input
                      type="text"
                      value={institutionData.address.street}
                      onChange={(e) => setInstitutionData(prev => ({
                        ...prev,
                        address: { ...prev.address, street: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        value={institutionData.address.city}
                        onChange={(e) => setInstitutionData(prev => ({
                          ...prev,
                          address: { ...prev.address, city: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input
                        type="text"
                        value={institutionData.address.state}
                        onChange={(e) => setInstitutionData(prev => ({
                          ...prev,
                          address: { ...prev.address, state: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                      <input
                        type="text"
                        value={institutionData.address.pincode}
                        onChange={(e) => setInstitutionData(prev => ({
                          ...prev,
                          address: { ...prev.address, pincode: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveInstitution}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  Save Institution Profile
                </button>
              </div>
            </motion.div>
          )}

          {/* Display Customization Tab */}
          {activeTab === 'display' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >

          {/* Basic Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-md p-6 mb-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Institution Name
                </label>
                <input
                  type="text"
                  value={customization.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tagline
                </label>
                <input
                  type="text"
                  value={customization.tagline}
                  onChange={(e) => handleInputChange('tagline', e.target.value)}
                  placeholder="A short, catchy tagline"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  About
                </label>
                <textarea
                  value={customization.about}
                  onChange={(e) => handleInputChange('about', e.target.value)}
                  rows={6}
                  placeholder="Describe your institution..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </motion.div>

          {/* Images */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-md p-6 mb-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Images</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo
                </label>
                {customization.logo && (
                  <div className="mb-2 relative">
                    <R2Image
                      fileKey={customization.logo}
                      alt="Logo"
                      className="w-32 h-32 object-contain border border-gray-300 rounded-lg"
                    />
                    <button
                      onClick={() => handleInputChange('logo', '')}
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <label className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload className="w-5 h-5" />
                  <span>Upload Logo</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleImageUpload(e.target.files[0], 'logo');
                      }
                    }}
                  />
                </label>
              </div>

              {/* Banner Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banner Image
                </label>
                {customization.bannerImage && (
                  <div className="mb-2 relative">
                    <R2Image
                      fileKey={customization.bannerImage}
                      alt="Banner"
                      className="w-full h-32 object-cover border border-gray-300 rounded-lg"
                    />
                    <button
                      onClick={() => handleInputChange('bannerImage', '')}
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <label className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload className="w-5 h-5" />
                  <span>Upload Banner</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleImageUpload(e.target.files[0], 'bannerImage');
                      }
                    }}
                  />
                </label>
              </div>
            </div>
          </motion.div>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-md p-6 mb-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Features</h2>
            
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                placeholder="Add a feature..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
              />
              <button
                onClick={addFeature}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2">
              {customization.features.map((feature, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <span>{feature}</span>
                  <button
                    onClick={() => removeFeature(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Programs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-md p-6 mb-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Programs Offered</h2>
            
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newProgram}
                onChange={(e) => setNewProgram(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addProgram()}
                placeholder="Add a program..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
              />
              <button
                onClick={addProgram}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2">
              {customization.programs.map((program, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <span>{program}</span>
                  <button
                    onClick={() => removeProgram(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Custom Sections */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-lg shadow-md p-6 mb-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Custom Sections</h2>
            
            <div className="space-y-4 mb-4">
              <input
                type="text"
                value={newSection.title}
                onChange={(e) => setNewSection(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Section Title"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <textarea
                value={newSection.content}
                onChange={(e) => setNewSection(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Section Content"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <button
                onClick={addCustomSection}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add Section
              </button>
            </div>

            <div className="space-y-4">
              {customization.customSections.map((section, index) => (
                <div key={index} className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{section.title}</h3>
                    <button
                      onClick={() => removeCustomSection(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-gray-600">{section.content}</p>
                </div>
              ))}
            </div>
          </motion.div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveCustomization}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Save Customization
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {/* Application Process Tab */}
          {activeTab === 'application' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Application Fee */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Application Fee</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Application Fee (₹)</label>
                    <input
                      type="number"
                      value={institutionData.applicationFee}
                      onChange={(e) => setInstitutionData(prev => ({
                        ...prev,
                        applicationFee: parseFloat(e.target.value) || 0
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={applicationSettings.requirePayment}
                      onChange={(e) => setApplicationSettings(prev => ({
                        ...prev,
                        requirePayment: e.target.checked
                      }))}
                      className="w-4 h-4"
                    />
                    <label className="text-sm text-gray-700">Require payment for application</label>
                  </div>
                </div>
              </div>

              {/* Custom Fields */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Custom Application Fields</h2>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCustomField.label}
                      onChange={(e) => setNewCustomField(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="Field Label"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <select
                      value={newCustomField.type}
                      onChange={(e) => setNewCustomField(prev => ({ ...prev, type: e.target.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Textarea</option>
                      <option value="number">Number</option>
                      <option value="email">Email</option>
                      <option value="date">Date</option>
                    </select>
                    <label className="flex items-center gap-2 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={newCustomField.required}
                        onChange={(e) => setNewCustomField(prev => ({ ...prev, required: e.target.checked }))}
                      />
                      <span className="text-sm">Required</span>
                    </label>
                    <button
                      onClick={addCustomField}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {applicationSettings.customFields.map((field, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div>
                          <span className="font-medium">{field.label}</span>
                          <span className="text-sm text-gray-500 ml-2">({field.type})</span>
                          {field.required && <span className="text-red-500 text-sm ml-2">*</span>}
                        </div>
                        <button
                          onClick={() => removeCustomField(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Requirements */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Admission Requirements</h2>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newRequirement}
                      onChange={(e) => setNewRequirement(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addRequirement()}
                      placeholder="Add a requirement..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <button
                      onClick={addRequirement}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {applicationSettings.requirements.map((req, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <span>{req}</span>
                        <button
                          onClick={() => removeRequirement(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Application Instructions</h2>
                <textarea
                  value={applicationSettings.instructions}
                  onChange={(e) => setApplicationSettings(prev => ({
                    ...prev,
                    instructions: e.target.value
                  }))}
                  rows={6}
                  placeholder="Add instructions for applicants..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex justify-end gap-4">
                <button
                  onClick={handleSaveInstitution}
                  disabled={saving}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
                >
                  Save Institution Settings
                </button>
                <button
                  onClick={handleSaveCustomization}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Save Application Settings
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default InstitutionCustomization;

